import { handleSimpleLoopImport, type SimpleLoopImportResult } from './loopImporter'
import type { RoutingMode } from './types'

// Worklet URLs — declared at module level for reliable Vite static analysis
export const WORKLET_EQP = new URL('../worklets/eqp-processor.ts', import.meta.url)
export const WORKLET_COMP = new URL('../worklets/compressor-processor.ts', import.meta.url)
export const WORKLET_MB = new URL('../worklets/multiband-processor.ts', import.meta.url)
export const WORKLET_OD = new URL('../worklets/overdrive-processor.ts', import.meta.url)
export const WORKLET_TRANS = new URL('../worklets/transient-processor.ts', import.meta.url)
export const WORKLET_DELAY = new URL('../worklets/delay-processor.ts', import.meta.url)
export const WORKLET_LIM = new URL('../worklets/limiter-processor.ts', import.meta.url)
export const WORKLET_REV = new URL('../worklets/reverb-processor.ts', import.meta.url)
export const WORKLET_RS_HALL = new URL('../worklets/rs-hall-processor.ts', import.meta.url)
const WORKLET_PEAK = new URL('../worklets/peak-meter-processor.ts', import.meta.url)

/** Snapshot of all engine state needed for offline rendering. */
export interface EngineStateSnapshot {
  // Module enable flags
  eqEnabled: boolean
  mbEnabled: boolean
  compEnabled: boolean
  odEnabled: boolean
  transEnabled: boolean
  delayEnabled: boolean
  rsEnabled: boolean
  revEnabled: boolean
  limEnabled: boolean
  masterLimiterEnabled: boolean
  bypassed: boolean

  // Per-module dry/wet gains (dB)
  eqDryDb: number; eqWetDb: number
  mbDryDb: number; mbWetDb: number
  compDryDb: number; compWetDb: number
  odDryDb: number; odWetDb: number
  transDryDb: number; transWetDb: number
  delayDryDb: number; delayWetDb: number
  rsDryDb: number; rsWetDb: number
  revDryDb: number; revWetDb: number
  limDryDb: number; limWetDb: number

  // Master
  masterDb: number
  outputTrimDb: number
  masterLimiterThresholdDb: number

  // EQ params
  lowShelfDb: number; lowShelfFreq: number
  mid1Db: number; mid1Freq: number; mid1Q: number
  mid2Db: number; mid2Freq: number; mid2Q: number
  highShelfDb: number; highShelfFreq: number
  heatAmount: number

  // Compressor params
  compType: string
  compThresholdDb: number; compRatio: number
  compAttackMs: number; compReleaseMs: number
  compMakeupDb: number; compKnee: number
  compDistAmount: number; compDistMode: string; compDistPosition: string

  // Multiband params
  mbLowMidFreq: number; mbMidHighFreq: number
  mbLowGainDb: number; mbMidGainDb: number; mbHighGainDb: number
  mbLowDrive: number; mbMidDrive: number; mbHighDrive: number
  mbLowType: string; mbMidType: string; mbHighType: string
  mbGlobalDrive: number

  // Overdrive params
  odDriveType: string; odDriveAmount: number
  odTightHz: number; odToneHz: number; odBias: number; odOutputDb: number

  // Transient params
  transAttackGainDb: number; transSustainGainDb: number
  transHoldMs: number; transDecayMs: number; transOutputDb: number

  // Delay params
  delayMs: number; delayFeedback: number; delayMix: number
  delayMode: string; delaySyncEnabled: boolean; delayBpm: number
  delayDivision: string; delayTone: number; delaySaturation: number; delayWidth: number

  // Reverb params
  revEngine: 'A' | 'B'
  revRoomType: string
  revPredelayMs: number; revSize: number; revDecay: number
  revDamping: number; revBandwidth: number; revDensity: number
  revEarlyLate: number; revOutputDb: number
  revModRate: number; revModDepth: number
  rsRoomSize: number; rsPreDelayMs: number; rsFeedback: number
  rsDamping: number; rsDiffusion: number
  rsEarlySize: number; rsEarlyDecay: number
  rsModRate: number; rsModDepth: number
  rsHighCut: number; rsLowCut: number
  rsEarlyLateMix: number; rsOutputDb: number

  // Limiter params
  limThresholdDb: number; limCeilingDb: number
  limReleaseMs: number; limKnee: number
  limMode: string; limQuality: number
}

interface LoadLoopOptions {
  totalSteps: number
}

interface GraphNodes {
  eqDryGain: GainNode
  eqWetGain: GainNode
  eqMixGain: GainNode
  compDryGain: GainNode
  compWetGain: GainNode
  compMixGain: GainNode
  mbDryGain: GainNode
  mbWetGain: GainNode
  mbMixGain: GainNode
  mbAnalyser: AnalyserNode
  eqAnalyser: AnalyserNode
  odDryGain: GainNode
  odWetGain: GainNode
  odMixGain: GainNode
  transDryGain: GainNode
  transWetGain: GainNode
  transMixGain: GainNode
  delayDryGain: GainNode
  delayWetGain: GainNode
  delayMixGain: GainNode
  rsDryGain: GainNode
  rsWetGain: GainNode
  rsMixGain: GainNode
  revDryGain: GainNode
  revWetGainA: GainNode
  revWetGainB: GainNode
  revMixGain: GainNode
  limDryGain: GainNode
  limWetGain: GainNode
  limMixGain: GainNode
  masterGain: GainNode
  outputGain: GainNode
  eqNode: AudioWorkletNode | null
  compNode: AudioWorkletNode | null
  mbNode: AudioWorkletNode | null
  odNode: AudioWorkletNode | null
  transNode: AudioWorkletNode | null
  delayNode: AudioWorkletNode | null
  rsNode: AudioWorkletNode | null
  revNode: AudioWorkletNode | null
  limNode: AudioWorkletNode | null
  masterLimiterNode: DynamicsCompressorNode | null
  peakMeterLeft: AudioWorkletNode | null
  peakMeterRight: AudioWorkletNode | null
}

const DEFAULT_DRY_DB = -6
const DEFAULT_WET_DB = -6
export class AudioEngine {
  private audioContext: AudioContext | null = null
  private currentSource: AudioBufferSourceNode | null = null
  private nodes: GraphNodes | null = null
  private bypassed = false
  private onEnded?: () => void
  private workletReady = false
  private workletLoadPromise: Promise<void> | null = null
  private workletLoadError: Error | null = null
  private lowShelfDb = 0
  private mid1Db = 0
  private mid2Db = 0
  private highShelfDb = 0
  private lowShelfFreq = 80
  private mid1Freq = 700
  private mid2Freq = 3000
  private highShelfFreq = 8000
  private mid1Q = 0.5
  private mid2Q = 0.5
  private heatAmount = 0
  private eqDryDb = DEFAULT_DRY_DB
  private eqWetDb = DEFAULT_WET_DB
  private compDryDb = DEFAULT_DRY_DB
  private compWetDb = DEFAULT_WET_DB
  private eqEnabled = true
  private compEnabled = false
  private compType: 'peak' | 'rms' | 'peak-dynamics-lo' | 'rms-dynamics-lo' = 'peak'
  private compThresholdDb = 0
  private compRatio = 2
  private compAttackMs = 30
  private compReleaseMs = 200
  private compMakeupDb = 0
  private compKnee = 0.4
  private compDistAmount = 0
  private compDistMode: 'valve' | 'solid' = 'valve'
  private compDistPosition: 'pre' | 'post' = 'post'
  private mbEnabled = false
  private mbDryDb = DEFAULT_DRY_DB
  private mbWetDb = DEFAULT_WET_DB
  private mbLowMidFreq = 200
  private mbMidHighFreq = 4000
  private mbLowGainDb = 0
  private mbMidGainDb = 0
  private mbHighGainDb = 0
  private mbLowDrive = 0
  private mbMidDrive = 0
  private mbHighDrive = 0
  private mbLowType = 'tube-warm'
  private mbMidType = 'tube-warm'
  private mbHighType = 'tube-warm'
  private mbGlobalDrive = 1
  private mbSpectrum: Float32Array<ArrayBuffer> | null = null
  private eqSpectrum: Float32Array<ArrayBuffer> | null = null
  private odEnabled = false
  private odDryDb = DEFAULT_DRY_DB
  private odWetDb = DEFAULT_WET_DB
  private odDriveType = 'tube-warm'
  private odDriveAmount = 0
  private odTightHz = 80
  private odToneHz = 8000
  private odBias = 0
  private odOutputDb = 0
  private transEnabled = false
  private transDryDb = DEFAULT_DRY_DB
  private transWetDb = DEFAULT_WET_DB
  private transAttackGainDb = 0
  private transSustainGainDb = 0
  private transHoldMs = 0
  private transDecayMs = 30
  private transOutputDb = 0
  private delayEnabled = false
  private delayDryDb = 0
  private delayWetDb = DEFAULT_WET_DB
  private delayMs = 350
  private delayFeedback = 0.35
  private delayMix = 1
  private delayMode: 'stereo' | 'pingpong' = 'stereo'
  private delaySyncEnabled = false
  private delayBpm = 120
  private delayDivision = '1/8'
  private delayTone = 0.7
  private delaySaturation = 0.15
  private delayWidth = 1
  private rsEnabled = false
  private rsDryDb = 0
  private rsWetDb = -6
  private revEnabled = false
  private revDryDb = 0        // Reverb uses send/return: dry at unity, wet blended in
  private revWetDb = -6
  private revRoomType = 'medium-room'
  private revPredelayMs = 25 // ms
  private revSize = 0.5
  private revDecay = 0.5
  private revDamping = 0.6
  private revBandwidth = 0.75
  private revDensity = 0.6
  private revEarlyLate = 0.5
  private revOutputDb = 0
  private revModRate = 0.5
  private revModDepth = 0.7
  private revEngine: 'A' | 'B' = 'A'
  private rsRoomSize = 0.71
  private rsPreDelayMs = 8
  private rsFeedback = 0.48
  private rsDamping = 0.72
  private rsDiffusion = 0.45
  private rsEarlySize = 0.84
  private rsEarlyDecay = 0.23
  private rsModRate = 0.72
  private rsModDepth = 0.79
  private rsHighCut = 7620
  private rsLowCut = 65
  private rsEarlyLateMix = 0.54
  private rsOutputDb = 0
  private limEnabled = false
  private limDryDb = DEFAULT_DRY_DB
  private limWetDb = DEFAULT_WET_DB
  private limThresholdDb = 0
  private limCeilingDb = 0
  private limReleaseMs = 100
  private limKnee = 0.4
  private limMode: 'pumping' | 'normal' | 'smooth' = 'normal'
  private limQuality: 1 | 2 | 4 = 4
  private masterLimiterEnabled = false
  private masterGainReduction = 0
  private masterDb = 0
  private masterLimiterThresholdDb = -0.2
  private outputTrimDb = -0.2
  private peakDbLeft = -96
  private peakDbRight = -96
  private compReductionDb = 0
  private limReductionDb = 0
  private requestId = 0
  private compRequestId = 0
  private limRequestId = 0
  private levelCallback: ((left: number, right: number) => void) | null = null
  private compReductionCallback: ((reductionDb: number) => void) | null = null
  private limReductionCallback: ((reductionDb: number) => void) | null = null
  private startTime: number | null = null
  private bufferDuration = 0
  private playGeneration = 0 // cancellation token for in-flight play() calls

  constructor(onEnded?: () => void) {
    this.onEnded = onEnded
  }

  async getContext(): Promise<AudioContext> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext()
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
    return this.audioContext
  }

  private dbToGain(db: number): number {
    if (db === Number.NEGATIVE_INFINITY) return 0
    return Math.pow(10, db / 20)
  }

  async ensureWorkletLoaded() {
    const ctx = this.audioContext
    if (!ctx) return
    if (this.workletReady) return
    if (this.workletLoadError) throw this.workletLoadError
    if (!this.workletLoadPromise) {
      this.workletLoadPromise = (async () => {
        await ctx.audioWorklet.addModule(WORKLET_EQP)
        await ctx.audioWorklet.addModule(WORKLET_COMP)
        await ctx.audioWorklet.addModule(WORKLET_MB)
        await ctx.audioWorklet.addModule(WORKLET_OD)
        await ctx.audioWorklet.addModule(WORKLET_TRANS)
        await ctx.audioWorklet.addModule(WORKLET_DELAY)
        await ctx.audioWorklet.addModule(WORKLET_LIM)
        await ctx.audioWorklet.addModule(WORKLET_REV)
        await ctx.audioWorklet.addModule(WORKLET_RS_HALL)
        await ctx.audioWorklet.addModule(WORKLET_PEAK)
        this.workletReady = true
      })()
    }
    try {
      await this.workletLoadPromise
    } catch (err) {
      this.workletLoadError = err instanceof Error ? err : new Error('AudioWorklet load failed')
      this.workletLoadPromise = null
      throw this.workletLoadError
    }
  }

  private async createGraph(): Promise<GraphNodes> {
    const ctx = await this.getContext()
    await this.ensureWorkletLoaded()

    const eqDryGain = ctx.createGain()
    const eqWetGain = ctx.createGain()
    const eqMixGain = ctx.createGain()
    const compDryGain = ctx.createGain()
    const compWetGain = ctx.createGain()
    const compMixGain = ctx.createGain()
    const mbDryGain = ctx.createGain()
    const mbWetGain = ctx.createGain()
    const mbMixGain = ctx.createGain()
    const mbAnalyser = ctx.createAnalyser()
    const eqAnalyser = ctx.createAnalyser()
    const odDryGain = ctx.createGain()
    const odWetGain = ctx.createGain()
    const odMixGain = ctx.createGain()
    const transDryGain = ctx.createGain()
    const transWetGain = ctx.createGain()
    const transMixGain = ctx.createGain()
    const delayDryGain = ctx.createGain()
    const delayWetGain = ctx.createGain()
    const delayMixGain = ctx.createGain()
    const rsDryGain = ctx.createGain()
    const rsWetGain = ctx.createGain()
    const rsMixGain = ctx.createGain()
    const revDryGain = ctx.createGain()
    const revWetGainA = ctx.createGain()
    const revWetGainB = ctx.createGain()
    const revMixGain = ctx.createGain()
    const limDryGain = ctx.createGain()
    const limWetGain = ctx.createGain()
    const limMixGain = ctx.createGain()
    const masterGain = ctx.createGain()
    const outputGain = ctx.createGain()
    eqMixGain.gain.value = 1
    compMixGain.gain.value = 1
    mbMixGain.gain.value = 1
    odMixGain.gain.value = 1
    transMixGain.gain.value = 1
    delayMixGain.gain.value = 1
    rsMixGain.gain.value = 1
    revMixGain.gain.value = 1
    limMixGain.gain.value = 1
    masterGain.gain.value = this.dbToGain(this.masterDb)
    outputGain.gain.value = this.dbToGain(this.outputTrimDb)

    mbAnalyser.fftSize = 2048
    mbAnalyser.smoothingTimeConstant = 0.8
    mbAnalyser.minDecibels = -100
    mbAnalyser.maxDecibels = -20

    eqAnalyser.fftSize = 2048
    eqAnalyser.smoothingTimeConstant = 0.8
    eqAnalyser.minDecibels = -100
    eqAnalyser.maxDecibels = -20

    const eqNode = this.workletReady ? new AudioWorkletNode(ctx, 'eqp-wdf-processor') : null
    const compNode = this.workletReady ? new AudioWorkletNode(ctx, 'compressor-processor') : null
    const mbNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'multiband-processor', { numberOfOutputs: 2, outputChannelCount: [2, 2] })
      : null
    const odNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'overdrive-processor', { numberOfOutputs: 2, outputChannelCount: [2, 2] })
      : null
    const transNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'transient-processor', { numberOfOutputs: 2, outputChannelCount: [2, 2] })
      : null
    const delayNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'delay-processor', { numberOfOutputs: 2, outputChannelCount: [2, 2] })
      : null
    const rsNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'rs-hall-processor', { numberOfOutputs: 1, outputChannelCount: [2] })
      : null
    const revNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'reverb-processor', { numberOfOutputs: 1, outputChannelCount: [2] })
      : null
    const limNode = this.workletReady
      ? new AudioWorkletNode(ctx, 'limiter-processor', { numberOfOutputs: 2, outputChannelCount: [2, 2] })
      : null

    // Create limiter (DynamicsCompressorNode configured as limiter)
    const masterLimiterNode = ctx.createDynamicsCompressor()
    masterLimiterNode.threshold.value = this.masterLimiterThresholdDb // Very high threshold
    masterLimiterNode.knee.value = 0 // Hard knee
    masterLimiterNode.ratio.value = 20 // High ratio = limiting
    masterLimiterNode.attack.value = 0.003 // Fast attack
    masterLimiterNode.release.value = 0.1 // Fast release

    // Create peak meter processors for VU meters
    const peakMeterLeft = this.workletReady ? new AudioWorkletNode(ctx, 'peak-meter-processor') : null
    const peakMeterRight = this.workletReady ? new AudioWorkletNode(ctx, 'peak-meter-processor') : null

    eqDryGain.gain.value = this.dbToGain(this.eqDryDb)
    eqWetGain.gain.value = this.dbToGain(this.eqWetDb)
    compDryGain.gain.value = this.dbToGain(this.compDryDb)
    compWetGain.gain.value = this.dbToGain(this.compWetDb)
    mbDryGain.gain.value = this.dbToGain(this.mbDryDb)
    mbWetGain.gain.value = this.dbToGain(this.mbWetDb)
    odDryGain.gain.value = this.dbToGain(this.odDryDb)
    odWetGain.gain.value = this.dbToGain(this.odWetDb)
    transDryGain.gain.value = this.dbToGain(this.transDryDb)
    transWetGain.gain.value = this.dbToGain(this.transWetDb)
    delayDryGain.gain.value = this.dbToGain(this.delayDryDb)
    delayWetGain.gain.value = this.dbToGain(this.delayWetDb)
    if (!this.delayEnabled) {
      delayDryGain.gain.value = 1
      delayWetGain.gain.value = 0
    }
    rsDryGain.gain.value = this.dbToGain(this.rsDryDb)
    rsWetGain.gain.value = this.dbToGain(this.rsWetDb)
    if (!this.rsEnabled) {
      rsDryGain.gain.value = 1
      rsWetGain.gain.value = 0
    }
    revDryGain.gain.value = this.dbToGain(this.revDryDb)
    revWetGainA.gain.value = this.dbToGain(this.revWetDb)
    revWetGainB.gain.value = this.dbToGain(this.revWetDb)
    if (!this.revEnabled) {
      revDryGain.gain.value = 1
      revWetGainA.gain.value = 0
      revWetGainB.gain.value = 0
    } else {
      // Crossfade between engines
      if (this.revEngine === 'A') {
        revWetGainA.gain.value = this.dbToGain(this.revWetDb)
        revWetGainB.gain.value = 0
      } else {
        revWetGainA.gain.value = 0
        revWetGainB.gain.value = this.dbToGain(this.revWetDb)
      }
    }
    limDryGain.gain.value = this.dbToGain(this.limDryDb)
    limWetGain.gain.value = this.dbToGain(this.limWetDb)


    // Initialize EQ parameters with stored values
    if (eqNode) {
      eqNode.port.postMessage({ type: 'lowGain', value: this.lowShelfDb })
      eqNode.port.postMessage({ type: 'mid1Gain', value: this.mid1Db })
      eqNode.port.postMessage({ type: 'mid2Gain', value: this.mid2Db })
      eqNode.port.postMessage({ type: 'highGain', value: this.highShelfDb })
      eqNode.port.postMessage({ type: 'lowFreq', value: this.lowShelfFreq })
      eqNode.port.postMessage({ type: 'mid1Freq', value: this.mid1Freq })
      eqNode.port.postMessage({ type: 'mid2Freq', value: this.mid2Freq })
      eqNode.port.postMessage({ type: 'highFreq', value: this.highShelfFreq })
      eqNode.port.postMessage({ type: 'mid1Q', value: this.mid1Q })
      eqNode.port.postMessage({ type: 'mid2Q', value: this.mid2Q })
      eqNode.port.postMessage({ type: 'heat', value: this.heatAmount })
    }

    if (compNode) {
      compNode.port.postMessage({ type: 'compType', value: this.compType })
      compNode.port.postMessage({ type: 'threshold', value: this.compThresholdDb })
      compNode.port.postMessage({ type: 'ratio', value: this.compRatio })
      compNode.port.postMessage({ type: 'attack', value: this.compAttackMs })
      compNode.port.postMessage({ type: 'release', value: this.compReleaseMs })
      compNode.port.postMessage({ type: 'makeup', value: this.compMakeupDb })
      compNode.port.postMessage({ type: 'knee', value: this.compKnee })
      compNode.port.postMessage({ type: 'distAmount', value: this.compDistAmount })
      compNode.port.postMessage({ type: 'distMode', value: this.compDistMode })
      compNode.port.postMessage({ type: 'distPosition', value: this.compDistPosition })
      compNode.port.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'reduction') {
          this.compReductionDb = event.data.value ?? 0
          if (this.compReductionCallback) {
            this.compReductionCallback(this.compReductionDb)
          }
        }
      }
    }

    if (mbNode) {
      mbNode.port.postMessage({ type: 'lowMidFreq', value: this.mbLowMidFreq })
      mbNode.port.postMessage({ type: 'midHighFreq', value: this.mbMidHighFreq })
      mbNode.port.postMessage({ type: 'lowGain', value: this.mbLowGainDb })
      mbNode.port.postMessage({ type: 'midGain', value: this.mbMidGainDb })
      mbNode.port.postMessage({ type: 'highGain', value: this.mbHighGainDb })
      mbNode.port.postMessage({ type: 'lowDrive', value: this.mbLowDrive })
      mbNode.port.postMessage({ type: 'midDrive', value: this.mbMidDrive })
      mbNode.port.postMessage({ type: 'highDrive', value: this.mbHighDrive })
      mbNode.port.postMessage({ type: 'lowType', value: this.mbLowType })
      mbNode.port.postMessage({ type: 'midType', value: this.mbMidType })
      mbNode.port.postMessage({ type: 'highType', value: this.mbHighType })
      mbNode.port.postMessage({ type: 'globalDrive', value: this.mbGlobalDrive })
    }

    if (odNode) {
      odNode.port.postMessage({ type: 'driveType', value: this.odDriveType })
      odNode.port.postMessage({ type: 'drive', value: this.odDriveAmount })
      odNode.port.postMessage({ type: 'tightHz', value: this.odTightHz })
      odNode.port.postMessage({ type: 'toneHz', value: this.odToneHz })
      odNode.port.postMessage({ type: 'bias', value: this.odBias })
      odNode.port.postMessage({ type: 'outputDb', value: this.odOutputDb })
    }

    if (transNode) {
      transNode.port.postMessage({ type: 'attackGain', value: this.transAttackGainDb })
      transNode.port.postMessage({ type: 'sustainGain', value: this.transSustainGainDb })
      transNode.port.postMessage({ type: 'holdTime', value: this.transHoldMs })
      transNode.port.postMessage({ type: 'decay', value: this.transDecayMs })
      transNode.port.postMessage({ type: 'outputDb', value: this.transOutputDb })
    }

    if (delayNode) {
      delayNode.port.postMessage({ type: 'enabled', value: this.delayEnabled })
      delayNode.port.postMessage({ type: 'mode', value: this.delayMode })
      delayNode.port.postMessage({ type: 'width', value: this.delayWidth })
      delayNode.port.postMessage({ type: 'delayMs', value: this.delayMs })
      delayNode.port.postMessage({ type: 'feedback', value: this.delayFeedback })
      delayNode.port.postMessage({ type: 'mix', value: this.delayMix })
      delayNode.port.postMessage({ type: 'syncEnabled', value: this.delaySyncEnabled })
      delayNode.port.postMessage({ type: 'bpm', value: this.delayBpm })
      delayNode.port.postMessage({ type: 'division', value: this.delayDivision })
      delayNode.port.postMessage({ type: 'tone', value: this.delayTone })
      delayNode.port.postMessage({ type: 'saturation', value: this.delaySaturation })
    }

    if (revNode) {
      revNode.port.postMessage({ type: 'room-type', value: this.revRoomType })
      revNode.port.postMessage({ type: 'predelay', value: this.revPredelayMs })
      revNode.port.postMessage({ type: 'size', value: this.revSize })
      revNode.port.postMessage({ type: 'decay', value: this.revDecay })
      revNode.port.postMessage({ type: 'damping', value: this.revDamping })
      revNode.port.postMessage({ type: 'bandwidth', value: this.revBandwidth })
      revNode.port.postMessage({ type: 'density', value: this.revDensity })
      revNode.port.postMessage({ type: 'early-late', value: this.revEarlyLate })
      revNode.port.postMessage({ type: 'mod-rate', value: this.revModRate })
      revNode.port.postMessage({ type: 'mod-depth', value: this.revModDepth })
      revNode.port.postMessage({ type: 'output-db', value: this.revOutputDb })
    }
    if (rsNode) {
      rsNode.port.postMessage({ type: 'roomSize', value: this.rsRoomSize })
      rsNode.port.postMessage({ type: 'preDelay', value: this.rsPreDelayMs })
      rsNode.port.postMessage({ type: 'feedback', value: this.rsFeedback })
      rsNode.port.postMessage({ type: 'damping', value: this.rsDamping })
      rsNode.port.postMessage({ type: 'diffusion', value: this.rsDiffusion })
      rsNode.port.postMessage({ type: 'earlySize', value: this.rsEarlySize })
      rsNode.port.postMessage({ type: 'earlyDecay', value: this.rsEarlyDecay })
      rsNode.port.postMessage({ type: 'modRate', value: this.rsModRate })
      rsNode.port.postMessage({ type: 'modDepth', value: this.rsModDepth })
      rsNode.port.postMessage({ type: 'highCut', value: this.rsHighCut })
      rsNode.port.postMessage({ type: 'lowCut', value: this.rsLowCut })
      rsNode.port.postMessage({ type: 'earlyLateMix', value: this.rsEarlyLateMix })
      rsNode.port.postMessage({ type: 'output-db', value: this.rsOutputDb })
    }

    if (limNode) {
      limNode.port.postMessage({ type: 'threshold', value: this.limThresholdDb })
      limNode.port.postMessage({ type: 'ceiling', value: this.limCeilingDb })
      limNode.port.postMessage({ type: 'release', value: this.limReleaseMs })
      limNode.port.postMessage({ type: 'knee', value: this.limKnee })
      limNode.port.postMessage({ type: 'mode', value: this.limMode })
      limNode.port.postMessage({ type: 'quality', value: this.limQuality })
      limNode.port.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'reduction') {
          this.limReductionDb = event.data.value ?? 0
          if (this.limReductionCallback) {
            this.limReductionCallback(this.limReductionDb)
          }
        }
      }
    }

    return {
      eqDryGain,
      eqWetGain,
      eqMixGain,
      compDryGain,
      compWetGain,
      compMixGain,
      mbDryGain,
      mbWetGain,
      mbMixGain,
      mbAnalyser,
      eqAnalyser,
      odDryGain,
      odWetGain,
      odMixGain,
      transDryGain,
      transWetGain,
      transMixGain,
      delayDryGain,
      delayWetGain,
      delayMixGain,
      rsDryGain,
      rsWetGain,
      rsMixGain,
      revDryGain,
      revWetGainA,
      revWetGainB,
      revMixGain,
      limDryGain,
      limWetGain,
      limMixGain,
      masterGain,
      outputGain,
      eqNode,
      compNode,
      mbNode,
      odNode,
      transNode,
      delayNode,
      rsNode,
      revNode,
      limNode,
      masterLimiterNode,
      peakMeterLeft,
      peakMeterRight,
    }
  }

  setBypass(bypassed: boolean) {
    this.bypassed = bypassed
    // If currently playing, reconnect with new bypass state
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  getBypass(): boolean {
    return this.bypassed
  }

  private reconnectSource() {
    if (!this.currentSource || !this.nodes) return

    const {
      eqDryGain,
      eqWetGain,
      eqMixGain,
      compDryGain,
      compWetGain,
      compMixGain,
      mbDryGain,
      mbWetGain,
      mbMixGain,
      mbAnalyser,
      eqAnalyser,
      odDryGain,
      odWetGain,
      odMixGain,
      transDryGain,
      transWetGain,
      transMixGain,
      delayDryGain,
      delayWetGain,
      delayMixGain,
      rsDryGain,
      rsWetGain,
      rsMixGain,
      revDryGain,
      revWetGainA,
      revWetGainB,
      revMixGain,
      limDryGain,
      limWetGain,
      limMixGain,
      masterGain,
      outputGain,
      eqNode,
      compNode,
      mbNode,
      odNode,
      transNode,
      delayNode,
      rsNode,
      revNode,
      limNode,
      masterLimiterNode,
      peakMeterLeft,
      peakMeterRight,
    } = this.nodes
    const ctx = this.currentSource.context

    // Disconnect source from everything
    this.currentSource.disconnect()

    // Disconnect all nodes
    eqDryGain.disconnect()
    eqWetGain.disconnect()
    eqMixGain.disconnect()
    compDryGain.disconnect()
    compWetGain.disconnect()
    compMixGain.disconnect()
    mbDryGain.disconnect()
    mbWetGain.disconnect()
    mbMixGain.disconnect()
    mbAnalyser.disconnect()
    eqAnalyser.disconnect()
    odDryGain.disconnect()
    odWetGain.disconnect()
    odMixGain.disconnect()
    transDryGain.disconnect()
    transWetGain.disconnect()
    transMixGain.disconnect()
    delayDryGain.disconnect()
    delayWetGain.disconnect()
    delayMixGain.disconnect()
    rsDryGain.disconnect()
    rsWetGain.disconnect()
    rsMixGain.disconnect()
    revDryGain.disconnect()
    revWetGainA.disconnect()
    revWetGainB.disconnect()
    revMixGain.disconnect()
    limDryGain.disconnect()
    limWetGain.disconnect()
    limMixGain.disconnect()
    if (eqNode) eqNode.disconnect()
    if (compNode) compNode.disconnect()
    if (mbNode) mbNode.disconnect()
    if (odNode) odNode.disconnect()
    if (transNode) transNode.disconnect()
    if (delayNode) delayNode.disconnect()
    if (revNode) revNode.disconnect()
    if (rsNode) rsNode.disconnect()
    if (limNode) limNode.disconnect()
    if (masterLimiterNode) masterLimiterNode.disconnect()
    if (peakMeterLeft) peakMeterLeft.disconnect()
    if (peakMeterRight) peakMeterRight.disconnect()
    masterGain.disconnect()
    outputGain.disconnect()

    let lastNode: AudioNode

    if (this.bypassed) {
      lastNode = this.currentSource
    } else {
      const eqInput = this.currentSource
      let eqOutput: AudioNode

      if (this.eqEnabled && eqNode) {
        eqInput.connect(eqDryGain)
        eqInput.connect(eqNode)
        eqNode.connect(eqWetGain)
        eqDryGain.connect(eqMixGain)
        eqWetGain.connect(eqMixGain)
        eqOutput = eqMixGain
      } else {
        eqOutput = eqInput
      }

      // Tap EQ output for analyzer
      eqOutput.connect(eqAnalyser)

      let mbOutput: AudioNode
      if (this.mbEnabled && mbNode) {
        eqOutput.connect(mbNode)
        mbNode.connect(mbDryGain, 0)
        mbNode.connect(mbWetGain, 1)
        mbDryGain.connect(mbMixGain)
        mbWetGain.connect(mbMixGain)
        mbOutput = mbMixGain
      } else {
        mbOutput = eqOutput
      }

      // Tap multiband output for analyzer
      mbOutput.connect(mbAnalyser)

      let compOutput: AudioNode
      if (this.compEnabled && compNode) {
        mbOutput.connect(compDryGain)
        mbOutput.connect(compNode)
        compNode.connect(compWetGain)
        compDryGain.connect(compMixGain)
        compWetGain.connect(compMixGain)
        compOutput = compMixGain
      } else {
        compOutput = mbOutput
      }

      let odOutput: AudioNode
      if (this.odEnabled && odNode) {
        compOutput.connect(odNode)
        odNode.connect(odDryGain, 0)
        odNode.connect(odWetGain, 1)

        odDryGain.connect(odMixGain)
        odWetGain.connect(odMixGain)
        odOutput = odMixGain
      } else {
        odOutput = compOutput
      }

      let transOutput: AudioNode
      if (this.transEnabled && transNode) {
        odOutput.connect(transNode)
        transNode.connect(transDryGain, 0)
        transNode.connect(transWetGain, 1)
        transDryGain.connect(transMixGain)
        transWetGain.connect(transMixGain)
        transOutput = transMixGain
      } else {
        transOutput = odOutput
      }

      let delayOutput: AudioNode
      if (this.delayEnabled && delayNode) {
        transOutput.connect(delayNode)
        delayNode.connect(delayDryGain, 0)
        delayNode.connect(delayWetGain, 1)
        delayDryGain.connect(delayMixGain)
        delayWetGain.connect(delayMixGain)
        delayOutput = delayMixGain
      } else {
        delayOutput = transOutput
      }

      let rsOutput: AudioNode
      if (this.rsEnabled && rsNode) {
        delayOutput.connect(rsDryGain)
        delayOutput.connect(rsNode)
        rsNode.connect(rsWetGain)
        rsDryGain.connect(rsMixGain)
        rsWetGain.connect(rsMixGain)
        rsOutput = rsMixGain
      } else {
        rsOutput = delayOutput
      }

      let revOutput: AudioNode
      if (revNode) {
        rsOutput.connect(revDryGain)
        rsOutput.connect(revNode)
        // Both A/B wet gains are fed by the single reverb engine so the
        // crossfade in setReverbEngine() is audible whichever side is
        // selected (previously B was unconnected and selecting it muted
        // the wet path while offline renders still included it).
        revNode.connect(revWetGainA)
        revNode.connect(revWetGainB)
        revDryGain.connect(revMixGain)
        revWetGainA.connect(revMixGain)
        revWetGainB.connect(revMixGain)
        revOutput = revMixGain
      } else {
        revOutput = rsOutput
      }

      let limOutput: AudioNode
      if (this.limEnabled && limNode) {
        revOutput.connect(limNode)
        limNode.connect(limDryGain, 0)
        limNode.connect(limWetGain, 1)
        limDryGain.connect(limMixGain)
        limWetGain.connect(limMixGain)
        limOutput = limMixGain
      } else {
        limOutput = revOutput
      }

      lastNode = limOutput
    }

    // Insert master fader before limiter/output
    lastNode.connect(masterGain)
    let tapSource: AudioNode = masterGain

    // Connect limiter if enabled (not when bypassed)
    if (!this.bypassed && this.masterLimiterEnabled && masterLimiterNode) {
      masterGain.connect(masterLimiterNode)
      tapSource = masterLimiterNode
    }

    // Audio path stays intact: route tapSource to output
    tapSource.connect(outputGain)
    outputGain.connect(ctx.destination)

    // Tap signal for peak meters after final output trim
    const splitter = ctx.createChannelSplitter(2)
    outputGain.connect(splitter)

    if (peakMeterLeft) {
      splitter.connect(peakMeterLeft, 0)  // Left channel to peak meter
    }
    if (peakMeterRight) {
      splitter.connect(peakMeterRight, 1) // Right channel to peak meter
    }
  }

  setRoutingMode(_mode: RoutingMode) {
    // Routing is managed per-module in the UI layer
  }

  setEqDryDb(db: number) {
    this.eqDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.eqDryGain.gain.value = this.dbToGain(db)
  }

  setEqWetDb(db: number) {
    this.eqWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.eqWetGain.gain.value = this.dbToGain(db)
  }

  setCompDryDb(db: number) {
    this.compDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.compDryGain.gain.value = this.dbToGain(db)
  }

  setCompWetDb(db: number) {
    this.compWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.compWetGain.gain.value = this.dbToGain(db)
  }

  setMasterDb(db: number) {
    this.masterDb = db
    if (!this.nodes) return
    this.nodes.masterGain.gain.value = this.dbToGain(db)
  }

  setLowShelfGain(db: number) {
    this.lowShelfDb = db
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'lowGain', value: db })
    }
  }

  setHighShelfGain(db: number) {
    this.highShelfDb = db
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'highGain', value: db })
    }
  }

  setMid1Gain(db: number) {
    this.mid1Db = db
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'mid1Gain', value: db })
    }
  }

  setMid2Gain(db: number) {
    this.mid2Db = db
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'mid2Gain', value: db })
    }
  }

  setLowShelfFreq(freq: number) {
    this.lowShelfFreq = freq
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'lowFreq', value: freq })
    }
  }

  setHighShelfFreq(freq: number) {
    this.highShelfFreq = freq
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'highFreq', value: freq })
    }
  }

  setMid1Freq(freq: number) {
    this.mid1Freq = freq
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'mid1Freq', value: freq })
    }
  }

  setMid2Freq(freq: number) {
    this.mid2Freq = freq
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'mid2Freq', value: freq })
    }
  }

  setMid1Q(q: number) {
    this.mid1Q = q
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'mid1Q', value: q })
    }
  }

  setMid2Q(q: number) {
    this.mid2Q = q
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'mid2Q', value: q })
    }
  }

  setHeatAmount(amount: number) {
    this.heatAmount = amount
    if (this.nodes?.eqNode) {
      this.nodes.eqNode.port.postMessage({ type: 'heat', value: amount })
    }
  }

  setEqEnabled(enabled: boolean) {
    this.eqEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setCompEnabled(enabled: boolean) {
    this.compEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setCompType(value: 'peak' | 'rms' | 'peak-dynamics-lo' | 'rms-dynamics-lo') {
    this.compType = value
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'compType', value })
    }
  }

  setCompThreshold(db: number) {
    this.compThresholdDb = db
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'threshold', value: db })
    }
  }

  setCompRatio(value: number) {
    this.compRatio = value
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'ratio', value })
    }
  }

  setCompAttack(ms: number) {
    this.compAttackMs = ms
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'attack', value: ms })
    }
  }

  setCompRelease(ms: number) {
    this.compReleaseMs = ms
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'release', value: ms })
    }
  }

  setCompMakeup(db: number) {
    this.compMakeupDb = db
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'makeup', value: db })
    }
  }

  setCompKnee(value: number) {
    this.compKnee = value
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'knee', value })
    }
  }

  setCompDistAmount(value: number) {
    this.compDistAmount = value
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'distAmount', value })
    }
  }

  setCompDistMode(value: 'valve' | 'solid') {
    this.compDistMode = value
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'distMode', value })
    }
  }

  setCompDistPosition(value: 'pre' | 'post') {
    this.compDistPosition = value
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'distPosition', value })
    }
  }

  setMultibandEnabled(enabled: boolean) {
    this.mbEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setMultibandDryDb(db: number) {
    this.mbDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.mbDryGain.gain.value = this.dbToGain(db)
  }

  setMultibandWetDb(db: number) {
    this.mbWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.mbWetGain.gain.value = this.dbToGain(db)
  }

  setMultibandLowMidFreq(freq: number) {
    this.mbLowMidFreq = freq
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'lowMidFreq', value: freq })
    }
  }

  setMultibandMidHighFreq(freq: number) {
    this.mbMidHighFreq = freq
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'midHighFreq', value: freq })
    }
  }

  setMultibandLowGain(db: number) {
    this.mbLowGainDb = db
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'lowGain', value: db })
    }
  }

  setMultibandMidGain(db: number) {
    this.mbMidGainDb = db
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'midGain', value: db })
    }
  }

  setMultibandHighGain(db: number) {
    this.mbHighGainDb = db
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'highGain', value: db })
    }
  }

  setMultibandLowDrive(value: number) {
    this.mbLowDrive = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'lowDrive', value })
    }
  }

  setMultibandMidDrive(value: number) {
    this.mbMidDrive = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'midDrive', value })
    }
  }

  setMultibandHighDrive(value: number) {
    this.mbHighDrive = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'highDrive', value })
    }
  }

  setMultibandLowType(value: string) {
    this.mbLowType = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'lowType', value })
    }
  }

  setMultibandMidType(value: string) {
    this.mbMidType = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'midType', value })
    }
  }

  setMultibandHighType(value: string) {
    this.mbHighType = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'highType', value })
    }
  }

  setMultibandGlobalDrive(value: number) {
    this.mbGlobalDrive = value
    if (this.nodes?.mbNode) {
      this.nodes.mbNode.port.postMessage({ type: 'globalDrive', value })
    }
  }

  setOverdriveEnabled(enabled: boolean) {
    this.odEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setOverdriveDryDb(db: number) {
    this.odDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.odDryGain.gain.value = this.dbToGain(db)
  }

  setOverdriveWetDb(db: number) {
    this.odWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.odWetGain.gain.value = this.dbToGain(db)
  }

  setOverdriveToneHz(value: number) {
    this.odToneHz = value
    if (this.nodes?.odNode) {
      this.nodes.odNode.port.postMessage({ type: 'toneHz', value: value })
    }
  }

  setOverdriveTightHz(value: number) {
    this.odTightHz = value
    if (this.nodes?.odNode) {
      this.nodes.odNode.port.postMessage({ type: 'tightHz', value: value })
    }
  }

  setOverdriveBias(value: number) {
    this.odBias = value
    if (this.nodes?.odNode) {
      this.nodes.odNode.port.postMessage({ type: 'bias', value: value })
    }
  }

  setOverdriveOutputDb(value: number) {
    this.odOutputDb = value
    if (this.nodes?.odNode) {
      this.nodes.odNode.port.postMessage({ type: 'outputDb', value: value })
    }
  }

  setOverdriveType(value: string) {
    this.odDriveType = value
    if (this.nodes?.odNode) {
      this.nodes.odNode.port.postMessage({ type: 'driveType', value })
    }
  }

  setOverdriveDrive(amount: number) {
    this.odDriveAmount = amount
    if (this.nodes?.odNode) {
      this.nodes.odNode.port.postMessage({ type: 'drive', value: amount })
    }
  }

  setTransientEnabled(enabled: boolean) {
    this.transEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setTransientDryDb(db: number) {
    this.transDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.transDryGain.gain.value = this.dbToGain(db)
  }

  setTransientWetDb(db: number) {
    this.transWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.transWetGain.gain.value = this.dbToGain(db)
  }

  setDelayEnabled(enabled: boolean) {
    this.delayEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'enabled', value: enabled })
    }
  }

  setDelayDryDb(db: number) {
    this.delayDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.delayDryGain.gain.value = this.dbToGain(db)
  }

  setDelayWetDb(db: number) {
    this.delayWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.delayWetGain.gain.value = this.dbToGain(db)
  }

  setDelayTimeMs(ms: number) {
    this.delayMs = Math.max(1, Math.min(2000, ms))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'delayMs', value: this.delayMs })
    }
  }

  setDelayFeedback(value: number) {
    this.delayFeedback = Math.max(0, Math.min(0.98, value))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'feedback', value: this.delayFeedback })
    }
  }

  setDelayMix(value: number) {
    this.delayMix = Math.max(0, Math.min(1, value))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'mix', value: this.delayMix })
    }
  }

  setDelayMode(mode: 'stereo' | 'pingpong') {
    this.delayMode = mode
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'mode', value: mode })
    }
  }

  setDelaySync(enabled: boolean) {
    this.delaySyncEnabled = enabled
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'syncEnabled', value: enabled })
    }
  }

  setDelayBpm(bpm: number) {
    this.delayBpm = Math.max(20, Math.min(400, bpm))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'bpm', value: this.delayBpm })
    }
  }

  setDelayDivision(division: string) {
    this.delayDivision = division
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'division', value: division })
    }
  }

  setDelayTone(value: number) {
    this.delayTone = Math.max(0, Math.min(1, value))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'tone', value: this.delayTone })
    }
  }

  setDelaySaturation(value: number) {
    this.delaySaturation = Math.max(0, Math.min(1, value))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'saturation', value: this.delaySaturation })
    }
  }

  setDelayWidth(value: number) {
    this.delayWidth = Math.max(0, Math.min(1, value))
    if (this.nodes?.delayNode) {
      this.nodes.delayNode.port.postMessage({ type: 'width', value: this.delayWidth })
    }
  }

  setRsReverbEnabled(enabled: boolean) {
    this.rsEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setRsReverbDryDb(db: number) {
    this.rsDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.rsDryGain.gain.value = this.dbToGain(db)
  }

  setRsReverbWetDb(db: number) {
    this.rsWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.rsWetGain.gain.value = this.dbToGain(db)
  }

  setTransientAttackGain(db: number) {
    this.transAttackGainDb = db
    if (this.nodes?.transNode) {
      this.nodes.transNode.port.postMessage({ type: 'attackGain', value: db })
    }
  }

  setTransientSustainGain(db: number) {
    this.transSustainGainDb = db
    if (this.nodes?.transNode) {
      this.nodes.transNode.port.postMessage({ type: 'sustainGain', value: db })
    }
  }

  setTransientHoldTime(ms: number) {
    this.transHoldMs = ms
    if (this.nodes?.transNode) {
      this.nodes.transNode.port.postMessage({ type: 'holdTime', value: ms })
    }
  }

  setTransientDecay(ms: number) {
    this.transDecayMs = ms
    if (this.nodes?.transNode) {
      this.nodes.transNode.port.postMessage({ type: 'decay', value: ms })
    }
  }

  setTransientOutputDb(db: number) {
    this.transOutputDb = db
    if (this.nodes?.transNode) {
      this.nodes.transNode.port.postMessage({ type: 'outputDb', value: db })
    }
  }

  // ─── Reverb ────────────────────────────────────────────────────────────────

  setReverbEnabled(enabled: boolean) {
    this.revEnabled = enabled
    this.applyReverbBypassState()
  }

  setReverbEngine(engine: 'A' | 'B') {
    this.revEngine = engine
    if (!this.nodes || this.bypassed || !this.revEnabled) return
    const now = this.audioContext ? this.audioContext.currentTime : 0
    const timeConstant = 0.05
    if (engine === 'A') {
      this.nodes.revWetGainA.gain.setTargetAtTime(this.dbToGain(this.revWetDb), now, timeConstant)
      this.nodes.revWetGainB.gain.setTargetAtTime(0, now, timeConstant)
    } else {
      this.nodes.revWetGainA.gain.setTargetAtTime(0, now, timeConstant)
      this.nodes.revWetGainB.gain.setTargetAtTime(this.dbToGain(this.revWetDb), now, timeConstant)
    }
  }

  setReverbDryDb(db: number) {
    this.revDryDb = db
    if (!this.nodes || this.bypassed || !this.revEnabled) return
    this.nodes.revDryGain.gain.value = this.dbToGain(db)
  }

  setReverbWetDb(db: number) {
    this.revWetDb = db
    if (!this.nodes || this.bypassed || !this.revEnabled) return
    if (this.revEngine === 'A') {
      this.nodes.revWetGainA.gain.value = this.dbToGain(db)
    } else {
      this.nodes.revWetGainB.gain.value = this.dbToGain(db)
    }
  }

  private applyReverbBypassState() {
    if (!this.nodes || this.bypassed) return
    const { revDryGain, revWetGainA, revWetGainB } = this.nodes
    const ctx = this.audioContext
    const now = ctx ? ctx.currentTime : 0
    const timeConstant = 0.01

    if (this.revEnabled) {
      revDryGain.gain.setTargetAtTime(this.dbToGain(this.revDryDb), now, timeConstant)
      if (this.revEngine === 'A') {
        revWetGainA.gain.setTargetAtTime(this.dbToGain(this.revWetDb), now, timeConstant)
        revWetGainB.gain.setTargetAtTime(0, now, timeConstant)
      } else {
        revWetGainA.gain.setTargetAtTime(0, now, timeConstant)
        revWetGainB.gain.setTargetAtTime(this.dbToGain(this.revWetDb), now, timeConstant)
      }
    } else {
      revDryGain.gain.setTargetAtTime(1, now, timeConstant)
      revWetGainA.gain.setTargetAtTime(0, now, timeConstant)
      revWetGainB.gain.setTargetAtTime(0, now, timeConstant)
    }
  }

  setReverbRoomType(value: string) {
    this.revRoomType = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'room-type', value })
    }
  }

  setReverbPredelay(ms: number) {
    this.revPredelayMs = ms
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'predelay', value: ms })
    }
  }

  setReverbSize(value: number) {
    this.revSize = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'size', value })
    }
  }

  setReverbDecay(value: number) {
    this.revDecay = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'decay', value })
    }
  }

  setReverbDamping(value: number) {
    this.revDamping = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'damping', value })
    }
  }

  setReverbBandwidth(value: number) {
    this.revBandwidth = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'bandwidth', value })
    }
  }

  setReverbDensity(value: number) {
    this.revDensity = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'density', value })
    }
  }

  setReverbEarlyLate(value: number) {
    this.revEarlyLate = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'early-late', value })
    }
  }

  setReverbOutputDb(db: number) {
    this.revOutputDb = db
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'output-db', value: db })
    }
  }

  setReverbModRate(value: number) {
    this.revModRate = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'mod-rate', value })
    }
  }

  setReverbModDepth(value: number) {
    this.revModDepth = value
    if (this.nodes?.revNode) {
      this.nodes.revNode.port.postMessage({ type: 'mod-depth', value })
    }
  }

  // ─── RS Hall Reverb (Engine B) ───────────────────────────────────────────

  setRsRoomSize(value: number) {
    this.rsRoomSize = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'roomSize', value })
    }
  }

  setRsPreDelay(ms: number) {
    this.rsPreDelayMs = ms
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'preDelay', value: ms })
    }
  }

  setRsFeedback(value: number) {
    this.rsFeedback = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'feedback', value })
    }
  }

  setRsDamping(value: number) {
    this.rsDamping = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'damping', value })
    }
  }

  setRsDiffusion(value: number) {
    this.rsDiffusion = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'diffusion', value })
    }
  }

  setRsEarlySize(value: number) {
    this.rsEarlySize = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'earlySize', value })
    }
  }

  setRsEarlyDecay(value: number) {
    this.rsEarlyDecay = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'earlyDecay', value })
    }
  }

  setRsModRate(value: number) {
    this.rsModRate = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'modRate', value })
    }
  }

  setRsModDepth(value: number) {
    this.rsModDepth = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'modDepth', value })
    }
  }

  setRsHighCut(value: number) {
    this.rsHighCut = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'highCut', value })
    }
  }

  setRsLowCut(value: number) {
    this.rsLowCut = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'lowCut', value })
    }
  }

  setRsEarlyLateMix(value: number) {
    this.rsEarlyLateMix = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'earlyLateMix', value })
    }
  }

  setRsOutputDb(value: number) {
    this.rsOutputDb = value
    if (this.nodes?.rsNode) {
      this.nodes.rsNode.port.postMessage({ type: 'output-db', value })
    }
  }

  setLimiterModuleEnabled(enabled: boolean) {
    this.limEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
    }
  }

  setLimiterDryDb(db: number) {
    this.limDryDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.limDryGain.gain.value = this.dbToGain(db)
  }

  setLimiterWetDb(db: number) {
    this.limWetDb = db
    if (!this.nodes || this.bypassed) return
    this.nodes.limWetGain.gain.value = this.dbToGain(db)
  }

  setLimiterThreshold(db: number) {
    this.limThresholdDb = db
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'threshold', value: db })
    }
  }

  setLimiterCeiling(db: number) {
    this.limCeilingDb = db
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'ceiling', value: db })
    }
  }

  setLimiterRelease(ms: number) {
    this.limReleaseMs = ms
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'release', value: ms })
    }
  }

  setLimiterKnee(value: number) {
    this.limKnee = value
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'knee', value })
    }
  }

  setLimiterMode(value: 'pumping' | 'normal' | 'smooth') {
    this.limMode = value
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'mode', value })
    }
  }

  setLimiterQuality(value: 1 | 2 | 4) {
    this.limQuality = value
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'quality', value })
    }
  }

  setMasterLimiterEnabled(enabled: boolean) {
    this.masterLimiterEnabled = enabled
    if (this.currentSource && this.nodes) {
      this.reconnectSource()
      if (enabled) {
        this.startGainReductionMonitoring()
      }
    }
  }

  setMasterLimiterThreshold(db: number) {
    this.masterLimiterThresholdDb = db
    if (this.nodes?.masterLimiterNode) {
      this.nodes.masterLimiterNode.threshold.value = db
    }
  }

  setOutputTrimDb(db: number) {
    this.outputTrimDb = db
    if (this.nodes?.outputGain) {
      this.nodes.outputGain.gain.value = this.dbToGain(db)
    }
  }

  getMasterLimiterEnabled(): boolean {
    return this.masterLimiterEnabled
  }

  getGainReduction(): number {
    return this.masterGainReduction
  }

  requestLevels() {
    this.requestId += 1
    if (this.nodes?.peakMeterLeft) {
      this.nodes.peakMeterLeft.port.postMessage({ type: 'get-levels', requestId: this.requestId })
    }
    if (this.nodes?.peakMeterRight) {
      this.nodes.peakMeterRight.port.postMessage({ type: 'get-levels', requestId: this.requestId })
    }
  }

  onLevels(callback: (left: number, right: number) => void) {
    this.levelCallback = callback
    
    if (this.nodes?.peakMeterLeft) {
      this.nodes.peakMeterLeft.port.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'levels') {
          this.peakDbLeft = event.data.peakDb || -96
          if (this.levelCallback) {
            this.levelCallback(this.peakDbLeft, this.peakDbRight)
          }
        }
      }
    }
    
    if (this.nodes?.peakMeterRight) {
      this.nodes.peakMeterRight.port.onmessage = (event: MessageEvent) => {
        if (event.data?.type === 'levels') {
          this.peakDbRight = event.data.peakDb || -96
          if (this.levelCallback) {
            this.levelCallback(this.peakDbLeft, this.peakDbRight)
          }
        }
      }
    }
  }

  requestCompReduction() {
    this.compRequestId += 1
    if (this.nodes?.compNode) {
      this.nodes.compNode.port.postMessage({ type: 'get-reduction', requestId: this.compRequestId })
    }
  }

  onCompReduction(callback: (reductionDb: number) => void) {
    this.compReductionCallback = callback
  }

  requestLimiterReduction() {
    this.limRequestId += 1
    if (this.nodes?.limNode) {
      this.nodes.limNode.port.postMessage({ type: 'get-reduction', requestId: this.limRequestId })
    }
  }

  onLimiterReduction(callback: (reductionDb: number) => void) {
    this.limReductionCallback = callback
  }

  getMultibandSpectrum(bands = 36): Float32Array {
    if (!this.nodes?.mbAnalyser || !this.audioContext) {
      return new Float32Array(bands)
    }
    const analyser = this.nodes.mbAnalyser
    const binCount = analyser.frequencyBinCount
    if (!this.mbSpectrum || this.mbSpectrum.length !== binCount) {
      this.mbSpectrum = new Float32Array(binCount)
    }
    analyser.getFloatFrequencyData(this.mbSpectrum)

    const minDb = analyser.minDecibels
    const maxDb = analyser.maxDecibels
    const result = new Float32Array(bands)
    const nyquist = this.audioContext.sampleRate / 2
    const minFreq = 20
    const maxFreq = 20000
    const minLog = Math.log10(minFreq)
    const maxLog = Math.log10(maxFreq)

    for (let b = 0; b < bands; b += 1) {
      const start = Math.pow(10, minLog + (b / bands) * (maxLog - minLog))
      const end = Math.pow(10, minLog + ((b + 1) / bands) * (maxLog - minLog))
      const startIdx = Math.max(0, Math.floor((start / nyquist) * binCount))
      const endIdx = Math.min(binCount - 1, Math.ceil((end / nyquist) * binCount))
      let peak = minDb
      for (let i = startIdx; i <= endIdx; i += 1) {
        const v = this.mbSpectrum[i]
        if (v > peak) peak = v
      }
      const norm = (peak - minDb) / (maxDb - minDb)
      result[b] = Math.max(0, Math.min(1, norm))
    }

    return result
  }

  getEqSpectrum(bands = 36): Float32Array {
    if (!this.nodes?.eqAnalyser || !this.audioContext) {
      return new Float32Array(bands)
    }
    const analyser = this.nodes.eqAnalyser
    const binCount = analyser.frequencyBinCount
    if (!this.eqSpectrum || this.eqSpectrum.length !== binCount) {
      this.eqSpectrum = new Float32Array(binCount)
    }
    analyser.getFloatFrequencyData(this.eqSpectrum)

    const minDb = analyser.minDecibels
    const maxDb = analyser.maxDecibels
    const result = new Float32Array(bands)
    const nyquist = this.audioContext.sampleRate / 2
    const minFreq = 20
    const maxFreq = 20000
    const minLog = Math.log10(minFreq)
    const maxLog = Math.log10(maxFreq)

    for (let b = 0; b < bands; b += 1) {
      const start = Math.pow(10, minLog + (b / bands) * (maxLog - minLog))
      const end = Math.pow(10, minLog + ((b + 1) / bands) * (maxLog - minLog))
      const startIdx = Math.max(0, Math.floor((start / nyquist) * binCount))
      const endIdx = Math.min(binCount - 1, Math.ceil((end / nyquist) * binCount))
      let peak = minDb
      for (let i = startIdx; i <= endIdx; i += 1) {
        const v = this.eqSpectrum[i]
        if (v > peak) peak = v
      }
      const norm = (peak - minDb) / (maxDb - minDb)
      result[b] = Math.max(0, Math.min(1, norm))
    }

    return result
  }

  getPeakDbLeft(): number {
    return this.peakDbLeft
  }

  getPeakDbRight(): number {
    return this.peakDbRight
  }

  private startGainReductionMonitoring() {
    const checkReduction = () => {
      if (this.masterLimiterEnabled && this.nodes?.masterLimiterNode) {
        const limiter = this.nodes.masterLimiterNode
        // DynamicsCompressorNode.reduction is a number in modern browsers
        const reductionValue = (limiter as unknown as { reduction: number }).reduction
        this.masterGainReduction = Number.isFinite(reductionValue) ? reductionValue : 0
      } else {
        this.masterGainReduction = 0
      }
      if (this.masterLimiterEnabled) {
        setTimeout(checkReduction, 50)
      }
    }
    checkReduction()
  }

  async loadLoop(file: File, opts: LoadLoopOptions): Promise<SimpleLoopImportResult> {
    const ctx = await this.getContext()
    return handleSimpleLoopImport(file, ctx, opts.totalSteps)
  }

  stop() {
    // Increment generation to cancel any in-flight play() calls
    this.playGeneration += 1

    if (this.currentSource) {
      this.currentSource.onended = null
      try {
        this.currentSource.stop()
      } catch (_err) {
        // ignore
      }
      this.currentSource.disconnect()
      this.currentSource = null
    }
    this.startTime = null
    this.bufferDuration = 0
  }

  getPlaybackTime(): number | null {
    if (!this.audioContext || this.startTime === null || this.bufferDuration <= 0) {
      return null
    }
    const elapsed = this.audioContext.currentTime - this.startTime
    if (!Number.isFinite(elapsed) || elapsed < 0) return null
    return elapsed % this.bufferDuration
  }

  async play(buffer: AudioBuffer, _routing: RoutingMode) {
    // Capture the generation at the start of this call.
    // If stop() is called while we're awaiting, generation will
    // increment and we'll bail out before starting the source.
    this.stop()
    const gen = this.playGeneration

    const ctx = await this.getContext()
    if (gen !== this.playGeneration) return // cancelled

    if (!this.nodes) {
      this.nodes = await this.createGraph()
      if (gen !== this.playGeneration) return // cancelled
    }

    // Start monitoring gain reduction if limiter is enabled
    if (this.masterLimiterEnabled && this.nodes?.masterLimiterNode) {
      this.startGainReductionMonitoring()
    }

    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.loop = true
    source.onended = () => {
      this.currentSource = null
      this.onEnded?.()
    }

    this.currentSource = source
    this.reconnectSource()
    this.bufferDuration = buffer.duration
    this.startTime = ctx.currentTime
    source.start(0)
  }

  /** Return a plain-object snapshot of every engine parameter for offline rendering. */
  getStateSnapshot(): EngineStateSnapshot {
    return {
      eqEnabled: this.eqEnabled,
      mbEnabled: this.mbEnabled,
      compEnabled: this.compEnabled,
      odEnabled: this.odEnabled,
      transEnabled: this.transEnabled,
      delayEnabled: this.delayEnabled,
      rsEnabled: this.rsEnabled,
      revEnabled: this.revEnabled,
      limEnabled: this.limEnabled,
      masterLimiterEnabled: this.masterLimiterEnabled,
      bypassed: this.bypassed,

      eqDryDb: this.eqDryDb, eqWetDb: this.eqWetDb,
      mbDryDb: this.mbDryDb, mbWetDb: this.mbWetDb,
      compDryDb: this.compDryDb, compWetDb: this.compWetDb,
      odDryDb: this.odDryDb, odWetDb: this.odWetDb,
      transDryDb: this.transDryDb, transWetDb: this.transWetDb,
      delayDryDb: this.delayDryDb, delayWetDb: this.delayWetDb,
      rsDryDb: this.rsDryDb, rsWetDb: this.rsWetDb,
      revDryDb: this.revDryDb, revWetDb: this.revWetDb,
      limDryDb: this.limDryDb, limWetDb: this.limWetDb,

      masterDb: this.masterDb,
      outputTrimDb: this.outputTrimDb,
      masterLimiterThresholdDb: this.masterLimiterThresholdDb,

      lowShelfDb: this.lowShelfDb, lowShelfFreq: this.lowShelfFreq,
      mid1Db: this.mid1Db, mid1Freq: this.mid1Freq, mid1Q: this.mid1Q,
      mid2Db: this.mid2Db, mid2Freq: this.mid2Freq, mid2Q: this.mid2Q,
      highShelfDb: this.highShelfDb, highShelfFreq: this.highShelfFreq,
      heatAmount: this.heatAmount,

      compType: this.compType,
      compThresholdDb: this.compThresholdDb, compRatio: this.compRatio,
      compAttackMs: this.compAttackMs, compReleaseMs: this.compReleaseMs,
      compMakeupDb: this.compMakeupDb, compKnee: this.compKnee,
      compDistAmount: this.compDistAmount, compDistMode: this.compDistMode,
      compDistPosition: this.compDistPosition,

      mbLowMidFreq: this.mbLowMidFreq, mbMidHighFreq: this.mbMidHighFreq,
      mbLowGainDb: this.mbLowGainDb, mbMidGainDb: this.mbMidGainDb, mbHighGainDb: this.mbHighGainDb,
      mbLowDrive: this.mbLowDrive, mbMidDrive: this.mbMidDrive, mbHighDrive: this.mbHighDrive,
      mbLowType: this.mbLowType, mbMidType: this.mbMidType, mbHighType: this.mbHighType,
      mbGlobalDrive: this.mbGlobalDrive,

      odDriveType: this.odDriveType, odDriveAmount: this.odDriveAmount,
      odTightHz: this.odTightHz, odToneHz: this.odToneHz,
      odBias: this.odBias, odOutputDb: this.odOutputDb,

      transAttackGainDb: this.transAttackGainDb, transSustainGainDb: this.transSustainGainDb,
      transHoldMs: this.transHoldMs, transDecayMs: this.transDecayMs,
      transOutputDb: this.transOutputDb,

      delayMs: this.delayMs, delayFeedback: this.delayFeedback, delayMix: this.delayMix,
      delayMode: this.delayMode, delaySyncEnabled: this.delaySyncEnabled, delayBpm: this.delayBpm,
      delayDivision: this.delayDivision, delayTone: this.delayTone,
      delaySaturation: this.delaySaturation, delayWidth: this.delayWidth,

      revEngine: this.revEngine,
      revRoomType: this.revRoomType,
      revPredelayMs: this.revPredelayMs, revSize: this.revSize, revDecay: this.revDecay,
      revDamping: this.revDamping, revBandwidth: this.revBandwidth, revDensity: this.revDensity,
      revEarlyLate: this.revEarlyLate, revOutputDb: this.revOutputDb,
      revModRate: this.revModRate, revModDepth: this.revModDepth,
      rsRoomSize: this.rsRoomSize, rsPreDelayMs: this.rsPreDelayMs, rsFeedback: this.rsFeedback,
      rsDamping: this.rsDamping, rsDiffusion: this.rsDiffusion,
      rsEarlySize: this.rsEarlySize, rsEarlyDecay: this.rsEarlyDecay,
      rsModRate: this.rsModRate, rsModDepth: this.rsModDepth,
      rsHighCut: this.rsHighCut, rsLowCut: this.rsLowCut,
      rsEarlyLateMix: this.rsEarlyLateMix, rsOutputDb: this.rsOutputDb,

      limThresholdDb: this.limThresholdDb, limCeilingDb: this.limCeilingDb,
      limReleaseMs: this.limReleaseMs, limKnee: this.limKnee,
      limMode: this.limMode, limQuality: this.limQuality,
    }
  }
}
